import { ReactElement, ReactNode, useRef } from 'react';
import { useB3Lang } from '@b3/lang';
import {
  Box,
  Breakpoint,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  SxProps,
  Theme,
} from '@mui/material';

import useMobile from '@/hooks/useMobile';
import useScrollBar from '@/hooks/useScrollBar';
import { useAppSelector } from '@/store';

import CustomButton from './button/CustomButton';
import B3Spin from './spin/B3Spin';

export interface B3DialogProps<T> {
  customActions?: () => ReactElement;
  isOpen: boolean;
  leftStyleBtn?: { [key: string]: string };
  rightStyleBtn?: { [key: string]: string };
  leftSizeBtn?: string;
  rightSizeBtn?: string;
  title?: string;
  handleLeftClick?: () => void;
  handRightClick?: (row?: T) => Promise<void> | void | undefined;
  children: ReactNode;
  loading?: boolean;
  row?: T;
  isShowBordered?: boolean;
  showRightBtn?: boolean;
  showLeftBtn?: boolean;
  maxWidth?: Breakpoint | false;
  fullWidth?: boolean;
  disabledSaveBtn?: boolean;
  dialogContentSx?: SxProps<Theme>;
  dialogSx?: SxProps<Theme>;
  dialogWidth?: string;
  restDialogParams?: Omit<DialogProps, 'open' | 'onClose'>;
}

export default function B3Dialog<T>({
  customActions,
  isOpen,
  leftStyleBtn = {},
  rightStyleBtn = {},
  leftSizeBtn,
  rightSizeBtn,
  title,
  handleLeftClick,
  handRightClick,
  children,
  loading = false,
  row,
  isShowBordered = true,
  showRightBtn = true,
  showLeftBtn = true,
  maxWidth = 'sm',
  dialogContentSx = {},
  dialogSx = {},
  fullWidth = false,
  disabledSaveBtn = false,
  dialogWidth = '',
  restDialogParams,
}: B3DialogProps<T>) {
  const container = useRef<HTMLInputElement | null>(null);

  const [isMobile] = useMobile();

  const isAgenting = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.isAgenting);

  const customStyle = dialogWidth
    ? {
        '& .MuiPaper-elevation': {
          width: isMobile ? '100%' : dialogWidth,
        },
        ...dialogSx,
      }
    : {
        ...dialogSx,
      };

  const handleSaveClick = () => {
    if (handRightClick) {
      if (row) handRightClick(row);
      if (!row) handRightClick();
    }
  };

  const handleCloseClick = (reason?: string) => {
    if (reason === 'backdropClick') return;
    if (handleLeftClick) handleLeftClick();
  };

  useScrollBar(isOpen);

  const b3Lang = useB3Lang();

  return (
    <Box>
      <Box ref={container} />

      <Dialog
        fullWidth={fullWidth}
        open={isOpen}
        container={container.current}
        onClose={(_: object, reason: string) => handleCloseClick(reason)}
        fullScreen={isMobile}
        maxWidth={maxWidth}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        id="b2b-dialog-container"
        sx={{
          ...customStyle,
          '.button': {
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            borderRadius: '4px',
            borderStyle: 'solid',
            borderWidth: '1px',
            cursor: 'pointer',
            fontFamily: '"Marine", "Helvetica", "Arial", sans-serif',
            fontWeight: 700,
            lineHeight: 'normal',
            position: 'relative',
            textAlign: 'center',
            textDecoration: 'none',
            display: 'inline-block',
            backgroundColor: '#fff',
            borderColor: '#004173',
            color: '#004173',
            fontSize: '0.875rem',
            padding: '0.75rem 1.5rem',
            outline: 0,
            margin: 0,
            verticalAlign: 'middle',
            textTransform: 'none',

            '& .MuiTouchRipple-root': {
              display: 'none',
            },

            '&:hover': {
              backgroundColor: '#004173',
              borderColor: '#004173',
              color: '#fff',
            },

            '&.Mui-disabled': {
              opacity: '0.5',
              color: '#004173',
            },
          },
          '.button--primary': {
            backgroundColor: '#0073a5',
            borderColor: '#0073a5',
            color: '#fff',

            '&:hover': {
              backgroundColor: '#004173',
              borderColor: '#004173',
              color: '#fff',
            },

            '&.Mui-disabled': {
              opacity: '0.5',
              color: '#fff',
            },
          },
          '& .MuiDialogContent-root': {
            fontFamily: '"Marine", "Helvetica", "Arial", sans-serif',

            '& .MuiList-root': {
              padding: '16px 0',
              borderBottom: '1px solid #D4D4D4',
              marginBottom: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              '& .MuiMenuItem-root': {
                padding: '8px',
                fontFamily: '"Marine", "Helvetica", "Arial", sans-serif',
                color: '#004173',
                borderRadius: '4px',

                '&:hover': {
                  backgroundColor: '#0073a5',
                  color: '#ffffff',
                },

                '&.active': {
                  backgroundColor: '#0073a5',
                  color: '#ffffff',
                },

                '& .MuiTouchRipple-root': {
                  display: 'none',
                },

                '& .MuiTypography-root': {
                  fontFamily: '"Marine", "Helvetica", "Arial", sans-serif',
                },
              },
            },
            '& .MuiButton-textPrimary': {
              gap: '4px',
              width: '100%',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              borderRadius: '4px',
              borderStyle: 'solid',
              borderWidth: '1px',
              cursor: 'pointer',
              fontFamily: '"Marine", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              lineHeight: 'normal',
              position: 'relative',
              textAlign: 'center',
              textDecoration: 'none',
              display: 'flex',
              backgroundColor: '#fff',
              borderColor: '#004173',
              color: '#004173',
              fontSize: '0.875rem',
              padding: '0.75rem 1.5rem',
              outline: 0,
              margin: 0,
              verticalAlign: 'middle',
              textTransform: 'none',

              '&.clear-filter': {
                mt: 2,
              },

              '& .MuiTouchRipple-root': {
                display: 'none',
              },

              '&:hover': {
                backgroundColor: '#004173',
                borderColor: '#004173',
                color: '#fff',
              },

              '&.Mui-disabled': {
                opacity: '0.75',
                color: '#004173',
              },
            },
            '& .MuiFormLabel-root': {
              fontFamily: '"Marine","Helvetica","Arial",sans-serif',
              position: 'static',
              transform: 'none',
              color: '#000',
              cursor: 'pointer',
              display: 'block',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: 1,
              mb: '0.5rem',
              maxWidth: '100%',
            },
            '& .MuiInputBase-root': {
              appearance: 'none',
              bgcolor: '#FFFFFF',
              borderRadius: '4px',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: '#d4d4d4',
              height: '44px',
              margin: 0,
              padding: '0.75rem 1rem',
              boxSizing: 'border-box',

              '& .Mui-focused': {
                bgcolor: '#d4d4d4',
              },
            },
            '& .MuiInputBase-multiline': {
              height: 'auto',
            },
            '& .MuiFilledInput-input': {
              padding: '0',
              fontSize: '14px',

              '&:focus': {
                background: 'transparent',
              },
            },
            '& .MuiFilledInput-root': {
              '&:before': {
                display: 'none',
              },
              '&::after': {
                display: 'none',
              },
            },
            '& .MuiGrid-root': {
              width: '100%',
              gap: '16px',
            },
            '& .MuiGrid-container': {
              margin: '0',
              '& .MuiGrid-item': {
                padding: '0',
              },
              '& .MuiBox-root': {
                fontFamily: '"Marine","Helvetica","Arial",sans-serif',
                fontSize: '13px',
                color: '#000',
              },
            },
            '& .Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
                borderColor: '#0073A5',
                boxShadow: 'none',
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 0,
              boxShadow: 'none',
            },
          },
        }}
        {...restDialogParams}
      >
        {title && (
          <DialogTitle
            sx={
              isShowBordered
                ? {
                    borderBottom: '1px solid #d4d4d4',
                    mb: '16px',
                    fontFamily: '"Marine","Helvetica","Arial",sans-serif',
                    color: '#000',
                    fontSize: '20px',
                    fontWeight: '700',
                    lineHeight: '1.5',
                  }
                : {
                    borderBottom: '1px solid #d4d4d4',
                    mb: '16px',
                    fontFamily: '"Marine","Helvetica","Arial",sans-serif',
                    color: '#000',
                    fontSize: '20px',
                    fontWeight: '700',
                    lineHeight: '1.5',
                  }
            }
            id="alert-dialog-title"
          >
            {title}
          </DialogTitle>
        )}
        <DialogContent
          sx={{
            ...dialogContentSx,
          }}
        >
          {children}
        </DialogContent>
        <DialogActions
          sx={
            isShowBordered
              ? {
                  borderTop: '1px solid #d4d4d4',
                  marginBottom: isAgenting && isMobile ? '52px' : '0',
                  display: 'flex',
                  gap: '8px',
                }
              : {
                  borderTop: '1px solid #d4d4d4',
                  marginBottom: isAgenting && isMobile ? '52px' : '0',
                  display: 'flex',
                  gap: '8px',
                }
          }
        >
          {customActions ? (
            customActions()
          ) : (
            <>
              {showLeftBtn && (
                <CustomButton
                  sx={{
                    ...leftStyleBtn,
                  }}
                  className="button button--secondary"
                  onClick={() => handleCloseClick('')}
                >
                  {leftSizeBtn || b3Lang('global.dialog.cancel')}
                </CustomButton>
              )}

              {showRightBtn && (
                <CustomButton
                  sx={{
                    ...rightStyleBtn,
                  }}
                  className="button button--primary"
                  onClick={handleSaveClick}
                  autoFocus
                  disabled={disabledSaveBtn || loading}
                >
                  <B3Spin isSpinning={loading} tip="" size={16} background="transparent">
                    {rightSizeBtn || b3Lang('global.dialog.save')}
                  </B3Spin>
                </CustomButton>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
